'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

interface CartItem {
    id: number;
    name: string;
    price: number;
    quantity: number;
    image: string;
}

// Key used for localStorage
const CART_STORAGE_KEY = 'vulnera_cart';

export default function CartPage() {
    const [cart, setCart] = useState<CartItem[]>([]);
    const [promoCode, setPromoCode] = useState('');
    const [promoApplied, setPromoApplied] = useState(false);

    // Load cart from localStorage on first render
    useEffect(() => {
        if (typeof window === 'undefined') return;

        try {
            const stored = localStorage.getItem(CART_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    setCart(parsed);
                    return;
                }
            }
            // If nothing in storage, start with empty cart
            setCart([]);
        } catch (e) {
            console.error('Failed to load cart from localStorage', e);
            setCart([]);
        }
    }, []);

    // Persist cart to localStorage whenever it changes
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
        } catch (e) {
            console.error('Failed to save cart to localStorage', e);
        }
    }, [cart]);

    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const discount = promoApplied ? subtotal * 0.1 : 0;
    const shipping = subtotal > 50 ? 0 : 9.99;
    const total = subtotal - discount + shipping;

    const updateQuantity = (id: number, delta: number) => {
        setCart(current =>
            current.map(item =>
                item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
            )
        );
    };

    const removeItem = (id: number) => {
        setCart(current => current.filter(item => item.id !== id));
    };

    const applyPromo = () => {
        if (promoCode) {
            setPromoApplied(true);
        }
    };

    return (
        <div className="relative">
            {/* Background */}
            <div className="absolute top-20 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[128px]" />

            <div className="max-w-6xl mx-auto px-4 py-12 relative">
                <div className="mb-10">
                    <span className="badge-primary mb-3 inline-block">Shopping</span>
                    <h1 className="text-4xl font-bold">Your Cart</h1>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {cart.map((item) => (
                            <div key={item.id} className="card flex gap-6 items-center">
                                <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 flex items-center justify-center text-4xl shrink-0">
                                    {item.image}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-white truncate">{item.name}</h3>
                                    <p className="text-gradient-primary font-bold text-lg">${item.price}</p>
                                    <div className="flex items-center gap-2 mt-3">
                                        <button
                                            onClick={() => updateQuantity(item.id, -1)}
                                            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                                        >−</button>
                                        <span className="w-10 text-center font-medium">{item.quantity}</span>
                                        <button
                                            onClick={() => updateQuantity(item.id, 1)}
                                            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                                        >+</button>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-xl text-white">${(item.price * item.quantity).toFixed(2)}</p>
                                    <button
                                        onClick={() => removeItem(item.id)}
                                        className="text-rose-400 text-sm mt-2 hover:text-rose-300 transition-colors"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ))}

                        {cart.length === 0 && (
                            <div className="card text-center py-16">
                                <div className="text-6xl mb-4">🛒</div>
                                <h3 className="text-xl font-semibold mb-2">Your cart is empty</h3>
                                <p className="text-slate-400 mb-6">Add some products to get started</p>
                                <Link href="/products" className="btn-primary inline-block">
                                    Browse Products
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="card sticky top-24">
                            <h2 className="text-xl font-bold mb-6">Order Summary</h2>

                            {/* Promo Code */}
                            <div className="mb-6">
                                <label className="block text-sm text-slate-400 mb-2">Promo Code</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={promoCode}
                                        onChange={(e) => setPromoCode(e.target.value)}
                                        placeholder="Enter code"
                                        className="input flex-1 text-sm"
                                    />
                                    <button onClick={applyPromo} className="btn-secondary text-sm px-4">
                                        Apply
                                    </button>
                                </div>
                                {/* VULNERABILITY: Reflected XSS */}
                                {promoApplied && (
                                    <p className="text-green-400 text-sm mt-2" dangerouslySetInnerHTML={{ __html: `✓ Code "${promoCode}" applied!` }} />
                                )}
                            </div>

                            <div className="space-y-3 text-sm border-t border-white/5 pt-6">
                                <div className="flex justify-between">
                                    <span className="text-slate-400">Subtotal</span>
                                    <span className="text-white">${subtotal.toFixed(2)}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-green-400">
                                        <span>Discount (10%)</span>
                                        <span>-${discount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-slate-400">Shipping</span>
                                    <span className={shipping === 0 ? 'text-green-400' : 'text-white'}>
                                        {shipping === 0 ? 'Free' : `$${shipping}`}
                                    </span>
                                </div>
                                <div className="border-t border-white/5 pt-4 flex justify-between font-bold text-lg">
                                    <span>Total</span>
                                    <span className="text-gradient-primary">${total.toFixed(2)}</span>
                                </div>
                            </div>

                            <Link href="/checkout" className="btn-primary w-full mt-6 text-center block py-4">
                                Proceed to Checkout
                            </Link>

                            <p className="text-center text-slate-500 text-xs mt-4">
                                🔒 Secure checkout powered by Stripe
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
